
import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

interface SearchBoxProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  initialQuery?: string;
  className?: string;
  autoFocus?: boolean;
  alwaysShowInput?: boolean;
}

export const SearchBox: React.FC<SearchBoxProps> = ({
  placeholder = 'Søk...',
  onSearch,
  initialQuery = '',
  className = '',
  autoFocus = false,
  alwaysShowInput = false,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [showInput, setShowInput] = useState(alwaysShowInput);
  const debouncedQuery = useDebounce(query, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const recentSearchesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (e) {
        console.error('Error parsing recent searches:', e);
        localStorage.removeItem('recentSearches');
      }
    }
  }, []);

  // Handle search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && debouncedQuery.trim() !== '') {
      handleSearch(debouncedQuery);
    }
  }, [debouncedQuery]);

  // Focus the input when it becomes visible
  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  // Handle click outside to close recent searches dropdown and search input
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        recentSearchesRef.current &&
        !recentSearchesRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowRecentSearches(false);
        if (!alwaysShowInput) {
          setShowInput(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [alwaysShowInput]);

  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim() === '') return;
    
    setIsSearching(true);
    
    try {
      // Call the search API
      const { data, error } = await supabase
        .from('interviews')
        .select('id, question, interviewee_name')
        .or(`question.ilike.%${searchQuery}%,interviewee_name.ilike.%${searchQuery}%,answer.ilike.%${searchQuery}%`)
        .limit(10);
      
      if (error) {
        console.error('Search error:', error);
        toast.error('Søket feilet. Prøv igjen senere.');
        return;
      }
      
      // Process search results
      
      // Save to recent searches if not already there
      if (searchQuery.trim() !== '') {
        const updatedSearches = [
          searchQuery,
          ...recentSearches.filter(s => s.toLowerCase() !== searchQuery.toLowerCase())
        ].slice(0, 5);
        
        setRecentSearches(updatedSearches);
        localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
      }
      
      // Call the onSearch callback if provided
      if (onSearch) {
        onSearch(searchQuery);
      } else {
        // Default behavior: navigate to search results page
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Søket feilet. Prøv igjen senere.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim() !== '') {
      handleSearch(query);
    }
  };

  const clearSearch = () => {
    setQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const selectRecentSearch = (search: string) => {
    setQuery(search);
    setShowRecentSearches(false);
    handleSearch(search);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
    setShowRecentSearches(false);
  };

  const toggleSearchInput = () => {
    setShowInput(!showInput);
    if (!showInput) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {showInput ? (
        <form onSubmit={handleSubmit} className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowRecentSearches(true)}
            className="pr-16"
            autoFocus={autoFocus}
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {query && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 mr-1"
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
            
            <Button
              type="submit"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={isSearching}
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="!h-[24px] !w-[24px] text-gray-500" />
              )}
              <span className="sr-only">Search</span>
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSearchInput}
          className="h-8 w-8 p-0"
        >
          <Search className="!h-[24px] !w-[24px] text-gray-500" />
          <span className="sr-only">Open search</span>
        </Button>
      )}
      
      {/* Recent searches dropdown */}
      {showRecentSearches && showInput && recentSearches.length > 0 && (
        <div
          ref={recentSearchesRef}
          className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200"
        >
          <div className="p-2">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Nylige søk</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                onClick={clearRecentSearches}
              >
                Tøm
              </Button>
            </div>
            <ul>
              {recentSearches.map((search, index) => (
                <li key={index}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 flex items-center"
                    onClick={() => selectRecentSearch(search)}
                  >
                    <Search className="h-3 w-3 mr-2 text-gray-400" />
                    {search}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
